import React from "react";
import { FormattedMessage } from "react-intl";
import {
  Heading,
  Box,
  Text,
  VStack,
  HStack,
  StackDivider,
  Divider,
  Spinner,
} from "@chakra-ui/react";
import { useFlowConfig } from "flows/hooks";
import { FormattedMoney } from "components/FormattedMoney";

/**
 * TransactionSummary - Presentational component for displaying the transaction summary of an invoice
 * @param {object} props
 * @param {Array.<LineItem>} props.lineItems
 * @param {string | null} [props.referenceType]
 * @param {string | null} [props.referenceId]
 * @param {number | null} [props.subtotalAmount]
 * @param {number | null} [props.taxTotalAmount]
 * @param {number | null} [props.totalAmount]
 * @param {number | null} [props.currency]
 * @param {boolean} [props.isLoadingNewTotal]
 * @returns JSX.Element
 */
export default function TransactionSummary({
  lineItems = [],
  referenceType,
  referenceId,
  subtotalAmount,
  taxTotalAmount,
  totalAmount,
  currency,
  isLoadingNewTotal = false,
  isTaxInclusive = false,
}) {
  const { hideSKUs, showInvoiceReferenceInfo } = useFlowConfig();
  return (
    <Box mb={8}>
      {/* TRANSACTION SUMMARY HEADER */}
      <Box pb={6}>
        <Heading>
          <FormattedMessage defaultMessage="Transaction summary" description="Section header" />
        </Heading>
        <Text size="sm" color="secondaryText">
          {showInvoiceReferenceInfo ? `${referenceType}${referenceId ? ` ${referenceId}` : ''}` : null}
        </Text>
      </Box>

      {/* LINE ITEMS */}
      <VStack spacing={3} divider={<StackDivider />} align="stretch">
        {lineItems.map((item, idx) => (
          <HStack key={`list-item-${idx}`} justify="space-between" align="flex-start">
            <Box>
              <Text>{item.description}</Text>
              {hideSKUs ? null : <Text color="tertiaryText">{item.product_sku}</Text>}
            </Box>
            <FormattedMoney value={item.amount} currency={item.currency} />
          </HStack>
        ))}
      </VStack>

      <VStack spacing={4} align="stretch" mt={3}>
        <Divider />

        {/* SUBTOTAL AND TAX */}
        {!isLoadingNewTotal && taxTotalAmount && !isTaxInclusive ? (
          <>
            <HStack justify="space-between" align="flex-start">
              <Text fontSize="md">
                <FormattedMessage defaultMessage="Subtotal" description="Row name" />
              </Text>
              <FormattedMoney fontSize="md" value={subtotalAmount} currency={currency || "USD"} />
            </HStack>
            <HStack justify="space-between" align="flex-start">
              <Text fontSize="md">
                <FormattedMessage defaultMessage="Tax" description="Row name" />
              </Text>
              <FormattedMoney fontSize="md" value={taxTotalAmount} currency={currency || "USD"} />
            </HStack>
            <Divider />
          </>
        ) : null}

        {/* TOTAL */}
        <HStack justify="space-between" align="flex-start">
          <Text fontSize="md" fontWeight="semibold">
            <FormattedMessage defaultMessage="Total" description="Row name" />
          </Text>

          {isLoadingNewTotal ? (
            <Spinner
              speed="0.5s"
              emptyColor="gray.200"
              color="integrator.500"
              data-testid="transaction-summary-total-spinner"
            />
          ) : (
            <VStack alignItems="flex-end">
              <FormattedMoney fontSize="md" fontWeight="semibold" value={totalAmount} currency={currency || "USD"} />
              {isTaxInclusive && taxTotalAmount && (
                <Text as="i" fontSize="sm">
                  *
                  <FormattedMessage
                    defaultMessage="Price is inclusive of tax"
                    description="Means - Tax is included in price."
                  />
                </Text>
              )}
            </VStack>
          )}
        </HStack>
        <Divider />
      </VStack>
    </Box>
  );
}
